// src/pages/DoctorTriageReportPage.jsx
// Route: /doctor/patients/:patientId/reports/:reportId
//
// "Patient view" of a triage report as its own page for the doctor portal (it
// used to open as a modal over the report). Shows the report exactly as the
// patient sees it, read-only.
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AppShell } from '../components/Layout';
import TriageReportModal from '../components/healthrecords/TriageReportModal';
import { fmtDate } from '../components/healthrecords/TriageReportModal';
import { adaptReport, adaptPatient } from '../components/doctor/DoctorTriageView';
import { doctorPatientAPI, triageReportAPI } from '../services/doctorApi';
import { useAuth } from '../context/AuthContext';

export default function DoctorTriageReportPage() {
  const { patientId, reportId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      doctorPatientAPI.getDetail(patientId, user?.email),
      triageReportAPI.listByPatient(patientId),
    ]).then(([pat, tri]) => {
      if (cancelled) return;
      const reports = tri.data?.content || tri.data || [];
      const report = reports.find(r => r.id === reportId);
      if (!report) { setError('Report not found.'); return; }
      setData({
        patient: adaptPatient(pat.data),
        triage: adaptReport(report),
        record: {
          id: report.id,
          drawDate: report.drawDate || report.createdAt,
          labName: report.triageType === 'BLOOD_TEST' ? 'Blood Test' : (report.triageType || 'Lab'),
          fileName: `Report · ${fmtDate(report.drawDate || report.createdAt)}`,
        },
        history: reports.filter(r => r.id !== report.id)
          .map(r => ({ ...adaptReport(r), drawDate: r.drawDate || r.createdAt })),
      });
    }).catch(() => { if (!cancelled) setError('Could not load the report. Please try again.'); });
    return () => { cancelled = true; };
  }, [patientId, reportId, user?.email]);

  const back = () => navigate(`/doctor/patients/${patientId}`);

  return (
    <AppShell breadcrumbs={[
      { label: 'My Patients', to: '/doctor/patients' },
      { label: 'Patient', to: `/doctor/patients/${patientId}` },
      { label: 'Patient view' },
    ]}>
      <span className="back-link fade-in-up" onClick={back}>← Back to patient</span>
      {error && <div className="alert alert-error" role="alert">{error}</div>}
      {!data && !error && (
        <div className="loading-state" style={{ padding: 40 }}>
          <div className="dot-loader"><span /><span /><span /></div>
          <div style={{ fontSize: 13 }}>Loading report…</div>
        </div>
      )}
      {data && (
        <TriageReportModal
          asPage
          readOnly
          triageResult={data.triage}
          healthRecord={data.record}
          patient={data.patient}
          triageHistory={data.history}
          onClose={back}
        />
      )}
    </AppShell>
  );
}
